import React, { createContext, useState, useMemo, useEffect } from 'react';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
    // Function to get initial cart state from localStorage
    const getInitialCart = () => {
        try {
            const storedCart = localStorage.getItem('shoppingCart');
            return storedCart ? JSON.parse(storedCart) : { items: [], isOpen: false };
        } catch (error) {
            console.error("Failed to parse cart from localStorage", error);
            return { items: [], isOpen: false };
        }
    };

    const [cart, setCart] = useState(getInitialCart);

    // Effect to save cart to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('shoppingCart', JSON.stringify(cart));
        } catch (error) {
            console.error("Failed to save cart to localStorage", error);
        }
    }, [cart]);


    const addToCart = (itemToAdd) => {
        setCart(prevCart => {
            const existingItemIndex = prevCart.items.findIndex(i => i.id === itemToAdd.id);
            let items;

            if (existingItemIndex > -1) {
                // Item exists, update its quantity
                items = prevCart.items.map((item, index) =>
                    index === existingItemIndex
                        ? { ...item, quantity: item.quantity + itemToAdd.quantity }
                        : item
                );
            } else {
                // New item, add it to the cart
                items = [...prevCart.items, { ...itemToAdd }];
            }
            return { ...prevCart, items };
        });
    };

    const increaseQuantity = (id) => {
        setCart(prevCart => {
            const items = prevCart.items.map(item =>
                item.id === id ? { ...item, quantity: item.quantity + 1 } : item
            );
            return { ...prevCart, items };
        });
    };

    const decreaseQuantity = (id) => {
        setCart(prevCart => {
            const items = prevCart.items.map(item =>
                item.id === id ? { ...item, quantity: item.quantity - 1 } : item
            ).filter(item => item.quantity > 0); // Remove item if quantity becomes 0

            return {
                ...prevCart,
                items,
                isOpen: items.length > 0 ? prevCart.isOpen : false, // Close cart if empty
            };
        });
    };

    const toggleCart = () => {
        setCart(prevCart => ({ ...prevCart, isOpen: !prevCart.isOpen }));
    };

    const updateQuantity = (id, newQuantity) => {
        const num = parseInt(newQuantity, 10);
        if (isNaN(num) || num <= 0) return;
        setCart(prevCart => {
            const items = prevCart.items.map(item =>
                item.id === id ? { ...item, quantity: num } : item
            );
            return { ...prevCart, items };
        });
    };

    const clearCart = () => {
        setCart({ items: [], isOpen: false });
    };

    const removeFromCart = (id) => {
        setCart(prevCart => {
            const updatedItems = prevCart.items.filter(item => item.id !== id);
            return {
                ...prevCart,
                items: updatedItems,
                isOpen: updatedItems.length > 0 ? prevCart.isOpen : false, // Close cart if empty
            };
        });
    };
    
    const updateItemUnit = (id, newUnit, newPrice, newFactor) => {
        setCart(prevCart => {
            const itemIndex = prevCart.items.findIndex(i => i.id === id);
            if (itemIndex === -1) return prevCart;

            const oldItem = prevCart.items[itemIndex];
            const baseId = id.split('_')[0]; // batch_id is before the underscore
            const newId = `${baseId}_${newUnit}`;

            // Check if there is already an item in the cart with the newId (i.e. same batch, same new unit)
            const existingIndex = prevCart.items.findIndex(i => i.id === newId);

            let items = [...prevCart.items];

            if (existingIndex > -1 && existingIndex !== itemIndex) {
                // Combine them!
                const existingItem = items[existingIndex];
                items[existingIndex] = {
                    ...existingItem,
                    quantity: existingItem.quantity + oldItem.quantity
                };
                // Remove the old item
                items = items.filter((_, idx) => idx !== itemIndex);
            } else {
                // Just update the old item's fields and id
                items[itemIndex] = {
                    ...oldItem,
                    id: newId,
                    selected_unit: newUnit,
                    display_unit: newUnit.toUpperCase(),
                    price: newPrice,
                    conversion_factor: newFactor
                };
            }

            return { ...prevCart, items };
        });
    };

    const cartValue = useMemo(() => ({
        cart,
        addToCart,
        increaseQuantity,
        decreaseQuantity,
        updateQuantity,
        toggleCart,
        clearCart,
        removeFromCart,
        updateItemUnit,
    }), [cart]);


    return (
        <CartContext.Provider value={cartValue}>
            {children}
        </CartContext.Provider>
    );
};
